<template>
    <div
        :class="[$style['mds-search-field'], classObject, sizeClass, variationClass]"
        :data-mds-version="version"
        role="search"
        :tabindex="tabIndex"
        :aria-labelledby="isSelection ? descriptionId : null"
    >
        <template v-if="isSelection">
            <div :id="descriptionId" aria-live="polite" :class="$style['mds-search-field__description']">
                {{ descriptionInfo }}
            </div>
            <mds-tag
                v-for="item in items"
                v-bind="removableTagAttributes"
                :id="item.tagId"
                :key="item.tagId"
                :text="item.text"
                :on-light="!onDark"
                :on-dark="onDark"
                :size="tagSize"
                @mds-tag-removed="!disabled ? handleRemoveClick(item) : null"
            >
            </mds-tag>
        </template>
        <input
            v-bind="attrsComputed"
            type="text"
            :class="$style['mds-search-field__input']"
            :disabled="disabled"
            :aria-label="label"
            :placeholder="placeholder"
            ref="input"
            :value="modelValueComputed"
            @input="$emit('update:modelValue', $event.target.value)"
            @focus="setFocus"
            @blur="removeFocus"
            @keydown.delete="handleRemoveKeyboard"
        />

        <mds-icon name="search" :size="iconSize" :class="$style['mds-search-field__search-icon']"></mds-icon>
        <mds-button
            :class="$style['mds-search-field__clear-button']"
            type="button"
            variation="icon-only"
            :size="iconSize"
            icon="remove"
            :on-dark="onDark"
            :text="clearIconAriaLabel"
            @click="clearSearchField($event)"
            :disabled="disableButton"
            v-show="showRemoveButton"
        ></mds-button>
        <span :class="$style['mds-search-field__input-focus-outline']"></span>
    </div>
</template>

<script>
    import { MdsButton } from '@mds/button-vue3';
    import { MdsTag } from '@mds/tag-vue3';
    import MdsIcon from '@mds/icon-vue3';
    import defaultLabels from './search-field_labels.json';
    import { MdsUniqueId } from '@mds/utils-js-vue3';

    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsSearchField',
        inheritAttrs: false,
        data() {
            return {
                isFocused: false,
                version: `${name}-${version}`,
            };
        },
        mixins: [MdsUniqueId],
        props: {
            clearIconAriaLabel: {
                type: String,
                default: 'Clear search field',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` on the icon-only clear button.',
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the `disabled` attribute.',
                },
            },
            label: {
                type: String,
                default: 'Search',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` for the search field.',
                },
            },
            labels: {
                type: Object,
                default: () => defaultLabels,
                docs: {
                    validation: '—',
                    description:
                        'Sets the label text used on the component. Individual labels can be overridden on an as-needed basis and will be merged with the default labels.',
                },
            },
            modelValue: {
                type: String,
                default: '',
                docs: {
                    validation: '—',
                    description: 'Sets the value string of the search field.',
                    support: ['v-model'],
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            placeholder: {
                type: String,
                default: 'Search...',
                docs: {
                    validation: '—',
                    description: 'Sets the placeholder text for the select.',
                },
            },
            selection: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, enables multiselection feature for the `input` variation.',
                },
            },
            selectedItems: {
                type: Array,
                default: () => [],
                docs: {
                    validation: '`selection` must be `true` and `variation` must be `input`',
                    description:
                        'An array of objects that sets the selected item(s) for the search field. The object must include a `"text"` property but supports any other custom properties. See the [variation example](?tab=variations#input-with-selection) and [usage example](#adding-and-removing-items-from-selecteditems) for more details.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`',
                    description: 'Sets the size of the search field.',
                },
            },
            variation: {
                type: String,
                default: 'primary',
                validator(value) {
                    return ['primary', 'secondary', 'input'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of `primary`, `secondary`, `input`.',
                    description: 'Set variation',
                },
            },
        },
        components: {
            MdsButton,
            MdsIcon,
            MdsTag,
        },
        emits: ['update:modelValue', 'mds-search-field-input-cleared', 'mds-search-field-item-deleted'],
        computed: {
            classObject() {
                const obj = {};

                obj[this.$style['mds-search-field--has-value']] = this.hasValue || this.selectedItems.length > 0;
                obj[this.$style['mds-search-field--selection']] = this.isSelection;
                obj[this.$style['mds-search-field--focus']] = this.isFocused;
                obj[this.$style['mds-search-field--disabled']] = this.disabled && this.isSelection;
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                // preserve class attribute getting applied to root element
                // https://v3-migration.vuejs.org/breaking-changes/attrs-includes-class-style.html
                if (this.$attrs.class) {
                    obj[this.$attrs.class] = true;
                }
                return obj;
            },
            attrsComputed() {
                // don't apply class attribute to <input>
                const { class: cssClass, ...rest } = this.$attrs;

                return rest;
            },
            sizeClass() {
                return this.$style[`mds-search-field--${this.size}`];
            },
            variationClass() {
                return this.$style[`mds-search-field--${this.variation}`];
            },
            iconSize() {
                return this.size === 'small' || this.size === 'medium' ? 'small' : 'medium';
            },
            tagSize() {
                return this.size === 'large' || this.size === 'touch' ? 'medium' : 'small';
            },
            tabIndex() {
                let index = null;

                if (this.isSelection) {
                    index = this.disabled ? -1 : 0;
                }

                return index;
            },
            disableButton() {
                const hasValues = !this.hasValue && this.isSelection && this.selectedItems.length === 0;
                return this.disabled || hasValues;
            },
            showRemoveButton() {
                return this.hasValue || (this.isSelection && this.selectedItems.length > 0);
            },
            isSelection() {
                return this.variation === 'input' && this.selection;
            },
            modelValueComputed() {
                return this.modelValue || this.$attrs.value;
            },
            hasValue() {
                return !!this.modelValueComputed;
            },
            items() {
                // Add tagId as `key` for looping
                const items = this.selectedItems.map(item => ({
                    ...item,
                    tagId: `${this.generateUuid('tag')}`,
                }));

                return items;
            },
            labelsMerged() {
                return Object.assign({}, defaultLabels, this.labels);
            },
            descriptionInfo() {
                const itemsInfo = this.items.map(item => item.text).join(', ');
                const descriptionLabel = this.labelsMerged.description || defaultLabels.description;

                // Set up labels for replacement in the `description`
                const labelReplacements = {
                    label: this.label,
                    itemsInfo,
                    itemsCount: this.items.length,
                };

                // Replace all occurrances of `{{placeholder}}` matching `key` with value
                const descriptionTemplate = descriptionLabel.replace(/{{(\w+)}}/g, (labelPlaceholder, labelKey) => {
                    return labelReplacements[labelKey] || labelPlaceholder;
                });

                return descriptionTemplate;
            },
            removableTagAttributes() {
                return !this.disabled
                    ? {
                          removable: true,
                          'remove-icon-aria-label': 'Remove',
                      }
                    : null;
            },
            descriptionId() {
                return this.generateUuid('search-field-description');
            },
        },
        methods: {
            handleRemoveKeyboard() {
                // for selection variation
                if (!this.hasValue && this.selectedItems.length > 0) {
                    const item = this.selectedItems[this.selectedItems.length - 1];
                    this.unselectItem(item);
                }
            },
            handleRemoveClick(item) {
                this.unselectItem(item);
            },
            unselectItem(item) {
                const { tagId, ...selectedItem } = item;
                this.$emit('mds-search-field-item-deleted', selectedItem);
            },
            setFocus() {
                if (!this.disabled && this.isSelection) {
                    this.isFocused = true;
                }
            },
            removeFocus() {
                this.isFocused = false;
            },
            clearSearchField(event) {
                const input = this.$refs.input;

                this.$emit('update:modelValue', '', event);

                // Custom event
                this.$emit('mds-search-field-input-cleared', event);

                // After clearing, bring focus back into the search field
                input.focus();
            },
        },
    };
</script>

<style lang="scss" module>
    @use './search-field.scss';
</style>
